import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  FormGroup,
} from '@mui/material';
import {
  Notifications,
  Email,
  Sms,
  Schedule,
  Settings,
  Science,
  Save,
  Refresh,
  Delete,
  Add,
  Edit,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

const AppointmentReminderSettings = ({ userRole = 'patient' }) => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    emailReminders: true,
    smsReminders: false,
    inAppReminders: true,
    reminderTimes: [24, 2, 0.5], // hours before appointment
    quietHours: {
      enabled: false,
      start: '22:00',
      end: '08:00'
    },
    reminderTypes: {
      appointmentConfirmation: true,
      appointmentReminder: true,
      appointmentCancellation: true,
      appointmentReschedule: true,
      followUpReminder: false
    }
  });
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testType, setTestType] = useState('appointmentReminder');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const result = await dispatch({
        type: 'appointments/getReminderSettings',
        payload: { userId: user.id }
      }).unwrap();
      
      setSettings(result);
    } catch (error) {
      console.error('Failed to load reminder settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      await dispatch({
        type: 'appointments/updateReminderSettings',
        payload: {
          userId: user.id,
          settings
        }
      }).unwrap();
      
      toast.success('Reminder settings saved successfully');
    } catch (error) {
      toast.error('Failed to save reminder settings');
    } finally {
      setLoading(false);
    }
  };

  const handleReminderTimeChange = (index, value) => {
    const newTimes = [...settings.reminderTimes];
    newTimes[index] = value;
    setSettings(prev => ({ ...prev, reminderTimes: newTimes }));
  };

  const addReminderTime = () => {
    setSettings(prev => ({
      ...prev,
      reminderTimes: [...prev.reminderTimes, 1]
    }));
  };

  const removeReminderTime = (index) => {
    if (settings.reminderTimes.length > 1) {
      const newTimes = settings.reminderTimes.filter((_, i) => i !== index);
      setSettings(prev => ({ ...prev, reminderTimes: newTimes }));
    }
  };

  const sendTestReminder = async () => {
    try {
      await dispatch({
        type: 'appointments/sendTestReminder',
        payload: {
          userId: user.id,
          type: testType
        }
      }).unwrap();
      
      toast.success('Test reminder sent successfully');
      setTestDialogOpen(false);
    } catch (error) {
      toast.error('Failed to send test reminder');
    }
  };

  const formatReminderTime = (hours) => {
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days > 1 ? 's' : ''} before`;
    } else if (hours >= 1) {
      return `${hours} hour${hours > 1 ? 's' : ''} before`;
    } else {
      const minutes = Math.floor(hours * 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} before`;
    }
  };

  if (loading && !settings.emailReminders) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Reminder Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Configure how and when you receive appointment reminders
      </Typography>

      <Grid container spacing={3}>
        {/* Notification Channels */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Notifications sx={{ mr: 1, verticalAlign: 'middle' }} />
                Notification Channels
              </Typography>
              
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailReminders}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        emailReminders: e.target.checked 
                      }))}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Email sx={{ mr: 1 }} />
                      Email Reminders
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.smsReminders}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        smsReminders: e.target.checked 
                      }))}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Sms sx={{ mr: 1 }} />
                      SMS Reminders
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.inAppReminders}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        inAppReminders: e.target.checked 
                      }))}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Notifications sx={{ mr: 1 }} />
                      In-App Notifications
                    </Box>
                  }
                />
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* Reminder Times */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  <Schedule sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Reminder Times
                </Typography>
                <Button
                  startIcon={<Add />}
                  onClick={addReminderTime}
                  size="small"
                >
                  Add Time
                </Button>
              </Box>
              
              <List>
                {settings.reminderTimes.map((time, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Schedule />
                    </ListItemIcon>
                    <ListItemText
                      primary={formatReminderTime(time)}
                      secondary="When to send reminder"
                    />
                    <Box sx={{ minWidth: 200, mr: 2 }}>
                      <Slider
                        value={time}
                        onChange={(_, value) => handleReminderTimeChange(index, value)}
                        min={0.25}
                        max={168} // 1 week
                        step={0.25}
                        marks={[
                          { value: 0.5, label: '30m' },
                          { value: 2, label: '2h' },
                          { value: 24, label: '1d' },
                          { value: 168, label: '1w' }
                        ]}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => formatReminderTime(value)}
                      />
                    </Box>
                    {settings.reminderTimes.length > 1 && (
                      <IconButton
                        onClick={() => removeReminderTime(index)}
                        color="error"
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Quiet Hours */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Settings sx={{ mr: 1, verticalAlign: 'middle' }} />
                Quiet Hours
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.quietHours.enabled}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      quietHours: { 
                        ...prev.quietHours, 
                        enabled: e.target.checked 
                      }
                    }))}
                  />
                }
                label="Enable quiet hours (no notifications during these times)"
              />
              
              {settings.quietHours.enabled && (
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="Start Time"
                    type="time"
                    value={settings.quietHours.start}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      quietHours: { 
                        ...prev.quietHours, 
                        start: e.target.value 
                      }
                    }))}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="End Time"
                    type="time"
                    value={settings.quietHours.end}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      quietHours: { 
                        ...prev.quietHours, 
                        end: e.target.value 
                      }
                    }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Reminder Types */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Reminder Types
              </Typography>
              
              <FormGroup>
                {Object.entries(settings.reminderTypes).map(([key, value]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        checked={value}
                        onChange={(e) => setSettings(prev => ({ 
                          ...prev, 
                          reminderTypes: { 
                            ...prev.reminderTypes, 
                            [key]: e.target.checked 
                          }
                        }))}
                      />
                    }
                    label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  />
                ))}
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<Science />}
          onClick={() => setTestDialogOpen(true)}
        >
          Send Test Reminder
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadSettings}
        >
          Reset
        </Button>
        
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={saveSettings}
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} /> : 'Save Settings'}
        </Button>
      </Box>

      {/* Test Reminder Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)}>
        <DialogTitle>Send Test Reminder</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Reminder Type</InputLabel>
            <Select
              value={testType}
              onChange={(e) => setTestType(e.target.value)}
            >
              <MenuItem value="appointmentReminder">Appointment Reminder</MenuItem>
              <MenuItem value="appointmentConfirmation">Appointment Confirmation</MenuItem>
              <MenuItem value="appointmentCancellation">Appointment Cancellation</MenuItem>
              <MenuItem value="appointmentReschedule">Appointment Reschedule</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={sendTestReminder}>
            Send Test
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppointmentReminderSettings;